import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { useCallbackRefState } from "@excalidraw/excalidraw/hooks/useCallbackRefState";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  exportToPlus,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import {
  isLoggedIn as checkIsLoggedIn,
  apiGetMe,
  apiGetDrawing,
  apiUpdateDrawing,
  apiUpdateDrawingKeepAlive,
  apiSaveFiles,
  apiGetSharedDrawing,
  apiListTags,
  apiSetDrawingTags,
  apiCreateTag,
} from "./data/api";
import { DrawingStore } from "./data/DrawingStore";
import { AuthDialog } from "./components/AuthDialog";
import { DrawingsDashboard } from "./components/DrawingsDashboard";
import { AdminPanel } from "./components/AdminPanel";
import { ElementComments } from "./components/ElementComments";
import "./components/DrawingsDashboard.scss";

import type { ApiUser, SharedDrawing, TagInfo } from "./data/api";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import {
  ExportToExcalidrawPlus,
  exportToExcalidrawPlus,
} from "./components/ExportToExcalidrawPlus";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

import { ExcalidrawPlusPromoBanner } from "./components/ExcalidrawPlusPromoBanner";
import { AppSidebar } from "./components/AppSidebar";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  // Guard: prevent cloud sync until initializeScene resolves and Excalidraw
  // processes the initial data (avoids overwriting DB with empty data on F5)
  const cloudSyncEnabledRef = useRef(false);

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [excalidrawAPI, excalidrawRefCallback] =
    useCallbackRefState<ExcalidrawImperativeAPI>();

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
      // Enable cloud sync only after Excalidraw has processed initialData,
      // preventing the first onChange (with empty/stale data) from overwriting the DB
      requestAnimationFrame(() => {
        cloudSyncEnabledRef.current = true;
      });
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      // Ignore drawing hash changes — handled by ExcalidrawApp
      if (isDrawingHash(window.location.hash)) {
        return;
      }
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
      flushCloudSync(true);
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
        flushCloudSync();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
      // Clean up module-level cloud sync state on unmount
      if (cloudSyncTimer) {
        clearTimeout(cloudSyncTimer);
        cloudSyncTimer = null;
      }
      pendingCloudSync = null;
      cloudSyncEnabledRef.current = false;
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();
      flushCloudSync(true);

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Sync to cloud API if user is logged in and has an active drawing
    // Skip sync until initializeScene has resolved to avoid overwriting DB with empty data
    const currentDrawingId = DrawingStore.getActiveDrawingId();
    if (currentDrawingId && checkIsLoggedIn() && cloudSyncEnabledRef.current) {
      syncDrawingToCloud(currentDrawingId, elements, appState, files);
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const ExcalidrawPlusCommand = {
    label: "Excalidraw+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };
  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "excalidraw",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        excalidrawAPI={excalidrawRefCallback}
        onChange={onChange}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
              renderCustomUI: excalidrawAPI
                ? (elements, appState, files) => {
                    return (
                      <ExportToExcalidrawPlus
                        elements={elements}
                        appState={appState}
                        files={files}
                        name={excalidrawAPI.getName()}
                        onError={(error) => {
                          excalidrawAPI?.updateScene({
                            appState: {
                              errorMessage: error.message,
                            },
                          });
                        }}
                        onSuccess={() => {
                          excalidrawAPI.updateScene({
                            appState: { openDialog: null },
                          });
                        }}
                      />
                    );
                  }
                : undefined,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile || !collabAPI || isCollabDisabled) {
            return null;
          }

          return (
            <div className="excalidraw-ui-top-right">
              {excalidrawAPI?.getEditorInterface().formFactor === "desktop" && (
                <ExcalidrawPlusPromoBanner
                  isSignedIn={isExcalidrawPlusSignedUser}
                />
              )}

              {collabError.message && <CollabError collabError={collabError} />}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
                editorInterface={editorInterface}
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
          {excalidrawAPI && (
            <OverwriteConfirmDialog.Action
              title={t("overwriteConfirm.action.excalidrawPlus.title")}
              actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
              onClick={() => {
                exportToExcalidrawPlus(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                  excalidrawAPI.getName(),
                );
              }}
            >
              {t("overwriteConfirm.action.excalidrawPlus.description")}
            </OverwriteConfirmDialog.Action>
          )}
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://github.com/excalidraw/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.followUs"),
              icon: XBrandIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["twitter", "contact", "social", "community"],
              perform: () => {
                window.open(
                  "https://x.com/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.discordChat"),
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              icon: DiscordIcon,
              keywords: [
                "chat",
                "talk",
                "contact",
                "bugs",
                "requests",
                "report",
                "feedback",
                "suggestions",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://discord.gg/UexuTaE",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: "YouTube",
              icon: youtubeIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["features", "tutorials", "howto", "help", "community"],
              perform: () => {
                window.open(
                  "https://youtube.com/@excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            ...(isExcalidrawPlusSignedUser
              ? [
                  {
                    ...ExcalidrawPlusAppCommand,
                    label: "Sign in / Go to Excalidraw+",
                  },
                ]
              : [ExcalidrawPlusCommand, ExcalidrawPlusAppCommand]),

            {
              label: t("overwriteConfirm.action.excalidrawPlus.button"),
              category: DEFAULT_CATEGORIES.export,
              icon: exportToPlus,
              predicate: true,
              keywords: ["plus", "export", "save", "backup"],
              perform: () => {
                if (excalidrawAPI) {
                  exportToExcalidrawPlus(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                    excalidrawAPI.getName(),
                  );
                }
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
      {excalidrawAPI && checkIsLoggedIn() && DrawingStore.getActiveDrawingId() && (
        <ElementComments excalidrawAPI={excalidrawAPI} />
      )}
    </div>
  );
};

// Cloud sync: debounced save to API when drawing changes
const CLOUD_SYNC_TIMEOUT = 200;
let cloudSyncTimer: ReturnType<typeof setTimeout> | null = null;
let pendingCloudSync: {
  drawingId: string;
  elements: readonly any[];
  appState: Partial<AppState>;
  files: BinaryFiles;
} | null = null;
// Fingerprint of last synced content — used to skip saves when nothing changed
let lastSyncedFingerprint: string | null = null;

type SyncStatus = "idle" | "saving" | "saved" | "error";
let onSyncStatusChange: ((status: SyncStatus) => void) | null = null;

// Computes a lightweight fingerprint of what matters for saving.
// Uses element versions (increment on edits) + file count.
// Ignores appState fields like scroll/zoom/selection that change constantly.
const computeFingerprint = (
  elements: readonly any[],
  files: BinaryFiles,
): string => {
  let versionSum = 0;
  for (const el of elements) {
    versionSum += el.version || 0;
  }
  return `${elements.length}:${versionSum}:${Object.keys(files).length}`;
};

const doCloudSync = async () => {
  const data = pendingCloudSync;
  if (!data || !checkIsLoggedIn() || !data.drawingId) {
    return;
  }
  pendingCloudSync = null;
  onSyncStatusChange?.("saving");
  try {
    await apiUpdateDrawing(data.drawingId, {
      elements: data.elements as any[],
      appState: data.appState as Record<string, any>,
    });
    const fileEntries = Object.entries(data.files);
    if (fileEntries.length > 0) {
      await apiSaveFiles(
        data.drawingId,
        fileEntries.map(([id, f]) => ({
          id,
          mimeType: (f as any).mimeType,
          dataURL: (f as any).dataURL,
        })),
      );
    }
    onSyncStatusChange?.("saved");
  } catch (err) {
    console.error("Cloud sync failed:", err);
    onSyncStatusChange?.("error");
  }
};

const syncDrawingToCloud = (
  drawingId: string,
  elements: readonly any[],
  appState: Partial<AppState>,
  files: BinaryFiles,
) => {
  // Skip if nothing meaningful changed (e.g., just selection or zoom)
  const fingerprint = computeFingerprint(elements, files);
  if (fingerprint === lastSyncedFingerprint) {
    return;
  }
  lastSyncedFingerprint = fingerprint;

  pendingCloudSync = { drawingId, elements, appState, files };
  if (cloudSyncTimer) {
    clearTimeout(cloudSyncTimer);
  }
  cloudSyncTimer = setTimeout(doCloudSync, CLOUD_SYNC_TIMEOUT);
};

// Reset the fingerprint — call when switching drawings
const resetCloudSyncFingerprint = () => {
  lastSyncedFingerprint = null;
};

// Flush pending cloud sync immediately
const flushCloudSync = (isUnload = false) => {
  if (cloudSyncTimer) {
    clearTimeout(cloudSyncTimer);
    cloudSyncTimer = null;
  }
  if (!pendingCloudSync) {
    return;
  }
  if (isUnload) {
    // Use keepalive fetch that survives page unload
    const data = pendingCloudSync;
    pendingCloudSync = null;
    if (data.drawingId && checkIsLoggedIn()) {
      apiUpdateDrawingKeepAlive(data.drawingId, {
        elements: data.elements as any[],
        appState: data.appState as Record<string, any>,
      });
    }
  } else {
    doCloudSync();
  }
};

type AppView = "auth" | "dashboard" | "editor" | "admin" | "viewer";

// ---------------------------------------------------------------------------
// URL hash helpers — human-readable drawing URLs
// Format: #/DrawingName--<uuid>
// ---------------------------------------------------------------------------
function buildDrawingHash(id: string, drawingName: string): string {
  const slug = encodeURIComponent((drawingName || "Sem-nome").replace(/\s+/g, "-"));
  return `#/${slug}--${id}`;
}

function parseDrawingFromHash(hash: string): { id: string } | null {
  // New format: #/Name--UUID
  const newFmt = hash.match(/--([a-f0-9-]{36})$/i);
  if (newFmt && hash.startsWith("#/")) {
    return { id: newFmt[1] };
  }
  // Legacy format: #drawing=UUID
  const legacy = hash.match(/^#drawing=([a-f0-9-]+)$/i);
  if (legacy) {
    return { id: legacy[1] };
  }
  return null;
}

function isDrawingHash(hash: string): boolean {
  return parseDrawingFromHash(hash) !== null;
}

/** Save drawing's folder_id so the dashboard opens in the right folder */
function saveDrawingFolder(folderId: string | null) {
  if (folderId) {
    sessionStorage.setItem("excalidraw-last-folder-id", folderId);
  }
}

const ExcalidrawApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  const [view, setView] = useState<AppView>(() => {
    // If there is a view-only share link, go to viewer
    if (window.location.hash.match(/^#view=[a-f0-9]+$/i)) {
      return "viewer";
    }
    // If there is a collaboration link or external scene, go straight to editor
    if (
      window.location.hash.includes("#room=") ||
      window.location.hash.includes("#json=") ||
      window.location.hash.includes("#url=")
    ) {
      return "editor";
    }
    // If URL has a drawing hash (new or legacy format), restore that drawing
    const parsedDrawing = parseDrawingFromHash(window.location.hash);
    if (parsedDrawing && checkIsLoggedIn()) {
      DrawingStore.setActiveDrawingId(parsedDrawing.id);
      return "editor";
    }
    // If logged in and has an active drawing, restore the editor
    const storedDrawingId = DrawingStore.getActiveDrawingId();
    if (checkIsLoggedIn() && storedDrawingId) {
      // Hash will be updated after drawing loads (with proper path)
      return "editor";
    }
    // If logged in, show dashboard; otherwise show auth
    return checkIsLoggedIn() ? "dashboard" : "auth";
  });

  const [user, setUser] = useState<ApiUser | null>(null);
  const [activeDrawingId, setActiveDrawingId] = useState<string | null>(
    DrawingStore.getActiveDrawingId(),
  );
  const [activeDrawingName, setActiveDrawingName] = useState<string>("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [isRestoring, setIsRestoring] = useState(() => {
    const drawingId = DrawingStore.getActiveDrawingId();
    return !!(checkIsLoggedIn() && drawingId && view === "editor");
  });
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");

  // Editor tag state
  const [allTags, setAllTags] = useState<TagInfo[]>([]);
  const [drawingTags, setDrawingTags] = useState<TagInfo[]>([]);
  const [showEditorTagPicker, setShowEditorTagPicker] = useState(false);
  const [newEditorTagName, setNewEditorTagName] = useState("");

  // View-only shared drawing state
  const [sharedDrawing, setSharedDrawing] = useState<SharedDrawing | null>(null);
  const [sharedError, setSharedError] = useState<string | null>(null);

  // Register sync status callback
  useEffect(() => {
    onSyncStatusChange = setSyncStatus;
    return () => { onSyncStatusChange = null; };
  }, []);

  // Auto-clear "saved" status after 3 seconds
  useEffect(() => {
    if (syncStatus === "saved") {
      const t = setTimeout(() => setSyncStatus("idle"), 3000);
      return () => clearTimeout(t);
    }
  }, [syncStatus]);

  // Load user and restore drawing from API on mount
  useEffect(() => {
    if (!checkIsLoggedIn()) {
      setIsRestoring(false);
      return;
    }

    apiGetMe()
      .then(setUser)
      .catch(() => {
        setIsRestoring(false);
        setView("auth");
      });

    const drawingId = DrawingStore.getActiveDrawingId();
    if (drawingId && view === "editor") {
      apiGetDrawing(drawingId)
        .then((drawing) => {
          setActiveDrawingName(drawing.name);
          setDrawingTags(drawing.tags || []);
          saveDrawingFolder(drawing.folder_id);
          window.location.hash = buildDrawingHash(drawingId, drawing.name);

          // Write fresh elements to localStorage
          localStorage.setItem(
            STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
            JSON.stringify(drawing.elements || []),
          );
          // Merge appState: preserve browser-only fields, server fields take priority
          const existing = JSON.parse(
            localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE) || "{}",
          );
          const server = drawing.appState || drawing.app_state || {};
          localStorage.setItem(
            STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
            JSON.stringify({ ...existing, ...server }),
          );
          apiListTags().then(setAllTags).catch(() => {});
        })
        .catch(() => {
          // Drawing not found or deleted — go to dashboard
          DrawingStore.setActiveDrawingId(null);
          window.history.replaceState({}, APP_NAME, window.location.pathname);
          setView("dashboard");
        })
        .finally(() => setIsRestoring(false));
    } else {
      setIsRestoring(false);
    }
  }, []);

  // Load shared drawing when in viewer mode
  useEffect(() => {
    if (view !== "viewer") {
      return;
    }
    const match = window.location.hash.match(/^#view=([a-f0-9]+)$/i);
    if (!match) {
      return;
    }
    apiGetSharedDrawing(match[1])
      .then(setSharedDrawing)
      .catch(() => setSharedError("Link de visualizacao invalido ou expirado."));
  }, [view]);

  const handleAuth = (authedUser: ApiUser) => {
    setUser(authedUser);
    setView("dashboard");
  };

  const handleSkipAuth = () => {
    setView("editor");
  };

  const handleOpenDrawing = async (id: string) => {
    resetCloudSyncFingerprint();
    setActiveDrawingId(id);
    DrawingStore.setActiveDrawingId(id);

    // Load drawing from API and set into localStorage so ExcalidrawWrapper picks it up
    try {
      const drawing = await apiGetDrawing(id);
      setActiveDrawingName(drawing.name);
      setDrawingTags(drawing.tags || []);
      saveDrawingFolder(drawing.folder_id);
      window.location.hash = buildDrawingHash(id, drawing.name);

      // Write to localStorage so the existing initializeScene picks it up
      localStorage.setItem(
        STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
        JSON.stringify(drawing.elements || []),
      );
      // Merge appState: preserve browser-only fields, server fields take priority
      const existingAppState = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE) || "{}",
      );
      const serverAppState = drawing.appState || drawing.app_state || {};
      localStorage.setItem(
        STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
        JSON.stringify({ ...existingAppState, ...serverAppState }),
      );
      // Load all tags for the picker
      apiListTags().then(setAllTags).catch(() => {});
    } catch (err) {
      console.error("Failed to load drawing:", err);
    }

    setView("editor");
  };

  const handleNewLocalDrawing = () => {
    resetCloudSyncFingerprint();
    // Clear localStorage for a fresh canvas
    localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
    localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
    setActiveDrawingId(null);
    DrawingStore.setActiveDrawingId(null);
    setActiveDrawingName("");
    window.history.replaceState({}, APP_NAME, window.location.pathname);
    setView("editor");
  };

  const handleSaveDrawingName = async () => {
    const trimmed = editingName.trim();
    if (trimmed && trimmed !== activeDrawingName && activeDrawingId) {
      try {
        await apiUpdateDrawing(activeDrawingId, { name: trimmed });
        setActiveDrawingName(trimmed);
        // Update URL hash with the new name
        window.location.hash = buildDrawingHash(activeDrawingId, trimmed);
      } catch (err) {
        console.error("Failed to rename drawing:", err);
      }
    }
    setIsEditingName(false);
  };

  const handleEditorToggleTag = async (tag: TagInfo) => {
    if (!activeDrawingId) return;
    const isActive = drawingTags.some((t) => t.id === tag.id);
    const newTags = isActive
      ? drawingTags.filter((t) => t.id !== tag.id)
      : [...drawingTags, tag];
    setDrawingTags(newTags);
    try {
      await apiSetDrawingTags(activeDrawingId, newTags.map((t) => t.id));
    } catch (err) {
      console.error("Failed to update tags:", err);
      setDrawingTags(drawingTags); // revert
    }
  };

  const handleEditorCreateTag = async () => {
    const name = newEditorTagName.trim();
    if (!name || !activeDrawingId) return;
    try {
      const tag = await apiCreateTag({ name });
      setAllTags((prev) => [...prev, tag]);
      const newTags = [...drawingTags, tag];
      setDrawingTags(newTags);
      await apiSetDrawingTags(activeDrawingId, newTags.map((t) => t.id));
      setNewEditorTagName("");
    } catch (err) {
      console.error("Failed to create tag:", err);
    }
  };

  const handleBackToDashboard = () => {
    // Force flush any pending saves
    LocalData.flushSave();
    flushCloudSync();
    setActiveDrawingId(null);
    DrawingStore.setActiveDrawingId(null);
    setActiveDrawingName("");
    window.history.replaceState({}, APP_NAME, window.location.pathname);
    setView("dashboard");
  };

  const handleLogout = () => {
    setUser(null);
    setActiveDrawingId(null);
    DrawingStore.setActiveDrawingId(null);
    window.history.replaceState({}, APP_NAME, window.location.pathname);
    setView("auth");
  };

  if (view === "viewer") {
    if (sharedError) {
      return (
        <TopErrorBoundary>
          <div className="viewer-error">
            <h2>Link indisponivel</h2>
            <p>{sharedError}</p>
          </div>
        </TopErrorBoundary>
      );
    }
    if (!sharedDrawing) {
      return (
        <TopErrorBoundary>
          <div className="dashboard-loading">Carregando visualizacao...</div>
        </TopErrorBoundary>
      );
    }
    return (
      <TopErrorBoundary>
        <Provider store={appJotaiStore}>
          <div className="viewer-header">
            <span className="viewer-header__title">{sharedDrawing.name}</span>
            <span className="viewer-header__author">
              por {sharedDrawing.authorName}
            </span>
            <span className="viewer-header__badge">Somente visualizacao</span>
          </div>
          <div style={{ height: "calc(100vh - 48px)", marginTop: 48 }}>
            <Excalidraw
              initialData={{
                elements: restoreElements(sharedDrawing.elements, null, {
                  repairBindings: true,
                  deleteInvisibleElements: true,
                }),
                appState: {
                  ...restoreAppState(sharedDrawing.appState, null),
                  viewModeEnabled: true,
                  collaborators: new Map(),
                },
                files: sharedDrawing.files || undefined,
                scrollToContent: true,
              }}
              viewModeEnabled={true}
              UIOptions={{
                canvasActions: {
                  toggleTheme: true,
                  export: false,
                  saveAsImage: true,
                },
              }}
            />
          </div>
        </Provider>
      </TopErrorBoundary>
    );
  }

  if (view === "auth") {
    return (
      <TopErrorBoundary>
        <AuthDialog onAuth={handleAuth} onSkip={handleSkipAuth} />
      </TopErrorBoundary>
    );
  }

  if (view === "admin") {
    return (
      <TopErrorBoundary>
        <AdminPanel onBack={() => setView("dashboard")} />
      </TopErrorBoundary>
    );
  }

  if (view === "dashboard") {
    return (
      <TopErrorBoundary>
        <DrawingsDashboard
          user={user}
          onOpenDrawing={handleOpenDrawing}
          onNewLocalDrawing={handleNewLocalDrawing}
          onLogout={handleLogout}
          onAdmin={() => setView("admin")}
        />
      </TopErrorBoundary>
    );
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        {isRestoring ? (
          <div className="dashboard-loading">Carregando quadro...</div>
        ) : (
          <ExcalidrawWrapper />
        )}
        {checkIsLoggedIn() && (
          <div className="editor-topbar">
            <button
              className="editor-topbar__back"
              onClick={handleBackToDashboard}
              title="Voltar ao dashboard"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <span className="editor-topbar__sep">/</span>

            {activeDrawingName && (
              isEditingName ? (
                <input
                  className="editor-topbar__name-input"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={handleSaveDrawingName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveDrawingName();
                    if (e.key === "Escape") setIsEditingName(false);
                  }}
                  autoFocus
                />
              ) : (
                <button
                  className="editor-topbar__name"
                  onClick={() => {
                    setEditingName(activeDrawingName);
                    setIsEditingName(true);
                  }}
                  title="Clique para renomear"
                >
                  {activeDrawingName}
                </button>
              )
            )}

            {activeDrawingId && (
              <>
                {drawingTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="editor-topbar__tag"
                    style={{ backgroundColor: tag.color + "18", color: tag.color, borderColor: tag.color + "40" }}
                  >
                    {tag.name}
                    <button
                      className="editor-topbar__tag-remove"
                      onClick={() => handleEditorToggleTag(tag)}
                      title="Remover tag"
                    >
                      &times;
                    </button>
                  </span>
                ))}
                <div className="editor-topbar__tag-picker">
                  <button
                    className="editor-topbar__add-tag"
                    onClick={() => setShowEditorTagPicker(!showEditorTagPicker)}
                  >
                    + Tag
                  </button>
                  {showEditorTagPicker && (
                    <div className="editor-topbar__tag-dropdown" onMouseLeave={() => setShowEditorTagPicker(false)}>
                      {allTags.filter((t) => !drawingTags.some((dt) => dt.id === t.id)).map((tag) => (
                        <button
                          key={tag.id}
                          className="editor-topbar__tag-option"
                          onClick={() => handleEditorToggleTag(tag)}
                        >
                          <span className="editor-topbar__tag-dot" style={{ backgroundColor: tag.color }} />
                          {tag.name}
                        </button>
                      ))}
                      <div className="editor-topbar__tag-create">
                        <input
                          type="text"
                          placeholder="Nova tag..."
                          value={newEditorTagName}
                          onChange={(e) => setNewEditorTagName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleEditorCreateTag();
                          }}
                          className="editor-topbar__tag-create-input"
                        />
                        {newEditorTagName.trim() && (
                          <button className="editor-topbar__tag-create-btn" onClick={handleEditorCreateTag}>
                            Criar
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {syncStatus !== "idle" && (
              <span className={`editor-topbar__sync editor-topbar__sync--${syncStatus}`}>
                {syncStatus === "saving" && "Salvando..."}
                {syncStatus === "saved" && "Salvo"}
                {syncStatus === "error" && "Erro ao salvar"}
              </span>
            )}
          </div>
        )}
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
